import React, { useEffect, useState } from 'react';
import { useAppDispatch } from 'hooks';

import { DOCUMENT_TYPES, LOAD_MORE_PAGE_SIZE } from '~constants/common';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useSearch } from '~libs/searchParams';
import { useToast } from '~libs/toast';
import { filterEmailableStatements } from '~support-at-home/lib/statements';

import { Button, ClickableIcon, ErrorResponse, Toolbar } from '~components';
import BulkActions from '~components/BulkActions';
import { SearchBar } from '~components/form-fields/search';
import Icon, { faMagnifyingGlass, faPlus } from '~components/icon';
import { TableView } from '~components/table-view';
import { useDocumentBatchGeneration } from '~main/components/document-batch-jobs/useDocumentBatchGeneration';

import { columns } from '~support-at-home/forms/statements/list';
import { searchFields } from '~support-at-home/forms/statements/search';

import { getAll as getStatementDeliveryPreferences } from '~lookups/actions/statementDeliveryPreferences';
import { getAll as getStatementStatuses } from '~lookups/actions/statementStatuses';
import { getAll as getClients } from '~main/actions/clients';
import { setTitle } from '~main/actions/login';
import { useGetStatementsQuery } from '~support-at-home/actions/statements';

import StatementsWizard from './statements-wizard';

const Statements: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearch('statements-search', { limit: LOAD_MORE_PAGE_SIZE });
  const [offset, setOffset] = useState<number>(0);

  const handleSearchSubmit = (values: Record<string, any>) => {
    setOffset(0);
    setSearchParams({ ...values, limit: LOAD_MORE_PAGE_SIZE });
  };

  const handleLoadMore = () => {
    if (isLoading) return;
    setOffset((current) => current + LOAD_MORE_PAGE_SIZE);
  };

  const [wizardOpen, setWizardOpen] = useState<boolean>(false);
  const [statementsSelected, setStatementsSelected] = useState<string[]>([]);

  useEffect(() => {
    if (isActive) {
      setTitle('Statements');
    }
    dispatch(getClients());
    dispatch(getStatementStatuses());
    dispatch(getStatementDeliveryPreferences());
  }, [isActive]);

  const {
    data,
    isFetching: isLoading,
    isError: hasStatementsError,
    error: statementsError,
  } = useGetStatementsQuery({
    ...searchParams,
    limit: LOAD_MORE_PAGE_SIZE,
    offset,
  });
  const { generateSeparate, generateCombined, emailSeparate } = useDocumentBatchGeneration();

  // TAV-2919 — a failed search (e.g. a filter combination the backend rejects) previously left
  // the table showing stale/unchanged results with no indication anything went wrong. `data` is
  // suppressed to `[]` here so the persistent banner below is never rendered under a contradictory
  // "no statements found" message (an `undefined` emptyMessage would trip TableView's own default).
  const statements = hasStatementsError ? [] : data?.rows || [];
  const total = data?.total;

  // Unified bulk action handler
  const handleBulkAction = async (action: string) => {
    if (!statementsSelected.length) return;
    try {
      switch (action) {
        case 'generate-separate':
          await generateSeparate(DOCUMENT_TYPES.SAH_STATEMENT.SINGLE, statementsSelected).unwrap();
          showToast('success', `Statement generation started for ${statementsSelected.length} statement(s)`);
          break;
        case 'generate-combined':
          await generateCombined(DOCUMENT_TYPES.SAH_STATEMENT.COMBINED, statementsSelected).unwrap();
          showToast('success', `Combined statement generation started for ${statementsSelected.length} statement(s)`);
          break;
        case 'email-separate': {
          const { emailableIds, skippedCount } = filterEmailableStatements(statementsSelected, statements);

          if (skippedCount > 0) {
            showToast('info', `${skippedCount} statement(s) skipped - client has no email address`);
          }

          if (emailableIds.length === 0) {
            showToast('danger', 'No selected statements have a client email address');
            return;
          }

          await emailSeparate(DOCUMENT_TYPES.SAH_STATEMENT.SINGLE, emailableIds).unwrap();
          showToast('success', `Statement emails scheduled for ${emailableIds.length} statement(s)`);
          break;
        }
        default:
          console.error('Unknown bulk action:', action);
          return;
      }
      setStatementsSelected([]);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Bulk action failed';
      showToast('danger', message);
      console.error('Bulk action failed:', error);
    }
  };

  const bulkActionOptions = [
    { value: 'generate-separate', label: 'Generate PDFs' },
    { value: 'generate-combined', label: 'Generate Combined PDF', minimum: 2 },
    { value: 'email-separate', label: 'Email Client Statements' },
  ];

  return (
    <div className="p-2 statements">
      <Toolbar
        id="statements"
        rightCluster={
          <>
            <BulkActions
              id="statements-bulk-actions"
              actionOptions={bulkActionOptions}
              onAction={handleBulkAction}
              selectedIds={statementsSelected}
            />
            <ClickableIcon
              className="me-2"
              icon={faMagnifyingGlass}
              id="statements-search-button"
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
            />
            <Button dataCy="statements-new-button" size="sm" color="success" onClick={() => setWizardOpen(true)}>
              <Icon className="me-2" icon={faPlus} />
              New
            </Button>
          </>
        }
      />
      <SearchBar
        isOpen={searchBarOpen}
        fields={searchFields}
        initialValues={searchParams}
        onSubmit={handleSearchSubmit}
      />
      {hasStatementsError && (
        <ErrorResponse message={`Failed to load statements - ${getErrorMessage(statementsError)}`} dataCy="statements-error" />
      )}
      <TableView
        id="statements-table"
        columns={columns}
        data={statements}
        isLoading={isLoading}
        emptyMessage={hasStatementsError ? '' : 'No statements found'}
        allowPagination
        totalCount={total}
        onLoadMore={handleLoadMore}
        readonly
        striped
        canSelect
        selectedIds={statementsSelected}
        onRowSelectionChange={(statementIds) =>
          setStatementsSelected((statementIds as (string | number)[]).map(String))
        }
      />
      <StatementsWizard isOpen={wizardOpen} setIsOpen={setWizardOpen} />
    </div>
  );
};

export default Statements;
